import { Badge, Button, Checkbox, clx, Container, Heading, Input, Label, Select, Text, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { dateTime, money } from "../lib/format"
import { t, tc } from "../lib/i18n"
import { errorMessage, hqApi, type OrderDetail, type OrderReturn, type ReturnWay } from "../lib/medusa"
import {
  exchangeChoices,
  isExtra,
  newReturnRequestId,
  problems,
  productOfVariant,
  REASONS,
  reasonLabel,
  returnable,
  returnedValue,
  suggestedRefund,
  WAYS,
  wayLabel,
  type Draft,
} from "../lib/returns"
import { Tabs } from "./ui"

const toAmount = (text: string) => Number(text.replace(",", ".").trim())
const sizeName = (option: string) => (option && !/^default/i.test(option) ? option : "")
const lineName = (title: string, option: string) => [title, sizeName(option)].filter(Boolean).join(" · ")

/** A small yes / no pair where neither is chosen until the owner picks one. */
function Choice({ value, onChange, yes, no, label }: { value: boolean | undefined; onChange: (v: boolean) => void; yes: string; no: string; label: string }) {
  const pill = (active: boolean) =>
    clx(
      "txt-compact-xsmall-plus h-8 rounded-full border px-3 transition-colors",
      active ? "bg-ui-fg-base text-ui-bg-base border-transparent" : "bg-ui-bg-base text-ui-fg-subtle border-ui-border-base hover:border-ui-border-strong"
    )
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      <button type="button" aria-pressed={value === true} className={pill(value === true)} onClick={() => onChange(true)}>{yes}</button>
      <button type="button" aria-pressed={value === false} className={pill(value === false)} onClick={() => onChange(false)}>{no}</button>
    </div>
  )
}

/**
 * Returns and exchanges recorded on a site order, near the bottom of its page. A new one is recorded
 * with Return at the top of the page (ReturnForm): what came back, unit by unit back on sale or not,
 * why and how, and the outcome: a refund (real money back to a card through Stripe; recorded for
 * cash) or an exchange (another size or product goes out, sent later with "Mark sent"). The customer
 * is emailed once for each, unless switched off.
 */
export function ReturnPanel({ order }: { order: OrderDetail }) {
  const returns = order.returns ?? []
  if (!returns.length) return null
  return (
    <Container className="flex flex-col gap-y-3">
      <Heading level="h2">{t("Returns and exchanges")}</Heading>
      {returns.map((entry) => <ReturnEntry key={entry.id} order={order} entry={entry} />)}
    </Container>
  )
}

function ReturnEntry({ entry }: { order: OrderDetail; entry: OrderReturn }) {
  const owed = entry.difference != null && entry.difference > 0 ? entry.difference : 0
  // The new item cost less and not all of the difference went back: the customer is still owed it.
  const kept = entry.difference != null && entry.difference < 0 ? Math.round((-entry.difference - (entry.refund_amount ?? 0)) * 100) / 100 : 0
  return (
    <div className="border-ui-border-base flex flex-col gap-y-2 border-t pt-3 first:border-t-0 first:pt-0">
      <Text size="small" className="text-ui-fg-subtle">
        {[dateTime(entry.created_at), reasonLabel(entry.reason), wayLabel(entry.way)].filter(Boolean).join(" · ")}
      </Text>
      <ul className="flex flex-col gap-y-1">
        {entry.items.map((item) => (
          <li key={item.line_item_id} className="flex flex-wrap items-center justify-between gap-x-2">
            <Text size="small">{lineName(item.title, item.option)}{item.quantity > 1 ? ` × ${item.quantity}` : ""}</Text>
            {!item.extra && (
              <Badge size="2xsmall" color={item.restock ? "green" : "grey"}>{item.restock ? t("Back on sale") : t("Not back on sale")}</Badge>
            )}
          </li>
        ))}
      </ul>
      {entry.outcome === "refund" ? (
        <Text size="small">{t("Refund {amount}", { amount: money(entry.refund_amount ?? 0) })}</Text>
      ) : (
        <div className="flex flex-col gap-y-1">
          <Text size="small" weight="plus">
            {t("Exchange: {items}", { items: entry.exchange_items.map((item) => `${lineName(item.title, item.option)}${item.quantity > 1 ? ` × ${item.quantity}` : ""}`).join(", ") })}
          </Text>
          {owed > 0 && <Text size="xsmall" className="text-ui-fg-subtle">{t("The customer owes {amount}.", { amount: money(owed) })}</Text>}
          {(entry.refund_amount ?? 0) > 0 && <Text size="xsmall" className="text-ui-fg-subtle">{t("Difference refunded: {amount}.", { amount: money(entry.refund_amount ?? 0) })}</Text>}
          {kept > 0 && <Text size="xsmall" className="text-ui-fg-subtle">{t("The new item costs {amount} less, and that was not refunded.", { amount: money(kept) })}</Text>}
          {entry.exchange_status === "sent" ? (
            <Text size="xsmall" className="text-ui-fg-subtle">
              {[t("Sent {date}", { date: dateTime(entry.exchange_sent_at) }), wayLabel(entry.exchange_way), entry.exchange_tracking].filter(Boolean).join(" · ")}
            </Text>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Badge size="2xsmall" color="orange">{t("To send")}</Badge>
              <Text size="xsmall" className="text-ui-fg-subtle">{t("Mark it sent at the top of the page.")}</Text>
            </div>
          )}
        </div>
      )}
      {entry.note && <Text size="xsmall" className="text-ui-fg-subtle">{entry.note}</Text>}
      <Text size="xsmall" className="text-ui-fg-muted">{entry.emailed ? t("The customer was emailed.") : t("No email was sent.")}</Text>
    </div>
  )
}

/** The exchange's new item went out: how, with BOX NOW's tracking number, and the customer's email. */
export function ExchangeSend({ order, entry }: { order: OrderDetail; entry: OrderReturn }) {
  const queryClient = useQueryClient()
  const [way, setWay] = useState<ReturnWay>(order.delivery === "box_now" ? "box_now" : "meetup")
  const [tracking, setTracking] = useState("")
  const [notify, setNotify] = useState(entry.emailed)
  const sent = useMutation({
    mutationFn: () => hqApi.exchangeSent(entry.id, { way, tracking_number: way === "box_now" ? tracking.trim() || null : null, notify }),
    onSuccess: (body) => {
      queryClient.setQueryData(["hq", "order", order.id], body.order)
      void queryClient.invalidateQueries({ queryKey: ["hq", "orders"] })
      toast.success(notify ? t("Marked sent. The customer is being emailed.") : t("Marked sent."))
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const ready = way !== "box_now" || tracking.trim().length >= 4
  return (
    <div className="flex flex-col gap-y-2.5">
      <Heading level="h2">{t("Send the exchange")}</Heading>
      <Text size="small" className="text-ui-fg-subtle">
        {entry.exchange_items.map((item) => `${lineName(item.title, item.option)}${item.quantity > 1 ? ` × ${item.quantity}` : ""}`).join(", ")}
      </Text>
      <div className="flex flex-col gap-y-1">
        <Label size="xsmall" weight="plus">{t("How it goes out")}</Label>
        <Select value={way} onValueChange={(value) => setWay(value as ReturnWay)}>
          <Select.Trigger><Select.Value /></Select.Trigger>
          <Select.Content>
            {WAYS.map((option) => <Select.Item key={option.key} value={option.key}>{option.label}</Select.Item>)}
          </Select.Content>
        </Select>
      </div>
      {way === "box_now" && (
        <Input inputMode="numeric" autoComplete="off" placeholder={t("Tracking number")} aria-label={t("BOX NOW tracking number")} value={tracking} onChange={(event) => setTracking(event.target.value)} />
      )}
      <div className="flex items-start gap-x-2">
        <Checkbox id={`notify-sent-${entry.id}`} checked={notify} onCheckedChange={(value) => setNotify(value === true)} className="mt-0.5" />
        <Label htmlFor={`notify-sent-${entry.id}`} className="txt-compact-small">{t("Email the customer that it is on its way")}</Label>
      </div>
      <Button size="small" className="w-fit" disabled={!ready} isLoading={sent.isPending} onClick={() => sent.mutate()}>{t("Mark sent")}</Button>
    </div>
  )
}

export function ReturnForm({ order, onClose }: { order: OrderDetail; onClose: () => void }) {
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const lines = returnable(order)
  const [requestId] = useState(newReturnRequestId)
  const [draft, setDraft] = useState<Draft>({
    picked: Object.fromEntries(lines.length === 1 ? [[lines[0].id, lines[0].returnable ?? 1]] : []),
    restock: {},
    reason: null,
    outcome: "refund",
    refund: "",
    exchange: { variant_id: null, quantity: 1 },
    refundDifference: false,
  })
  const [way, setWay] = useState<ReturnWay | null>(null)
  const [note, setNote] = useState("")
  const [notify, setNotify] = useState(true)
  const [refundTouched, setRefundTouched] = useState(false)
  const [productId, setProductId] = useState<string | null>(null)
  const card = order.payment.kind === "card"
  const refundable = order.payment.refundable

  const profit = useQuery({ queryKey: ["hq", "product-profit"], queryFn: () => hqApi.productProfit(), enabled: draft.outcome === "exchange" })
  const choices = useMemo(() => exchangeChoices(profit.data), [profit.data])

  // Start the exchange on the product that came back.
  useEffect(() => {
    if (draft.outcome !== "exchange" || productId || !profit.data) return
    const first = lines.find((line) => !isExtra(line) && (draft.picked[line.id] ?? 0) > 0)
    const product = productOfVariant(profit.data, first?.variant_id)
    if (product) setProductId(product.id)
  }, [draft.outcome, profit.data, productId, lines, draft.picked])

  const suggestion = suggestedRefund(order, draft.picked)
  useEffect(() => {
    if (!refundTouched) setDraft((d) => ({ ...d, refund: suggestion > 0 ? suggestion.toFixed(2) : "" }))
  }, [suggestion, refundTouched])

  const product = choices.find((c) => c.id === productId) ?? null
  const size = product?.sizes.find((s) => s.variant_id === draft.exchange.variant_id) ?? null
  // Units of the same size coming back in this return count: a swap of the same size always fits.
  const restocking = lines.filter((line) => line.variant_id === size?.variant_id && draft.restock[line.id] === true).reduce((sum, line) => sum + (draft.picked[line.id] ?? 0), 0)
  const available = size ? (size.available ?? 0) + restocking : null
  const back = returnedValue(order, draft.picked)
  const exchangeValue = product?.price != null ? product.price * draft.exchange.quantity : null
  const difference = exchangeValue != null ? Math.round((exchangeValue - back) * 100) / 100 : null
  const refundAmount = draft.outcome === "refund" ? toAmount(draft.refund) : draft.refundDifference && difference != null && difference < 0 ? Math.min(-difference, refundable) : 0
  const blocking = problems(order, draft, refundable, available)

  const save = useMutation({
    mutationFn: () =>
      hqApi.receiveReturn(order.id, {
        request_id: requestId,
        items: lines.filter((line) => (draft.picked[line.id] ?? 0) > 0).map((line) => ({ line_item_id: line.id, quantity: draft.picked[line.id], restock: isExtra(line) ? null : draft.restock[line.id] ?? null })),
        reason: draft.reason!,
        way,
        note: note.trim() || null,
        outcome: draft.outcome,
        refund_amount: refundAmount > 0 ? Math.round(refundAmount * 100) / 100 : null,
        exchange_items: draft.outcome === "exchange" && draft.exchange.variant_id ? [{ variant_id: draft.exchange.variant_id, quantity: draft.exchange.quantity }] : null,
        notify,
      }),
    onSuccess: (body) => {
      queryClient.setQueryData(["hq", "order", order.id], body.order)
      void queryClient.invalidateQueries({ queryKey: ["hq", "orders"] })
      void queryClient.invalidateQueries({ queryKey: ["hq", "product-profit"] })
      toast.success(body.result.replay ? t("Already recorded.") : notify ? t("Return recorded. The customer is being emailed.") : t("Return recorded."))
      onClose()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }))
  const pick = (id: string, quantity: number, max: number) => setDraft((d) => ({ ...d, picked: { ...d.picked, [id]: Math.max(0, Math.min(max, quantity)) } }))

  async function confirm() {
    if (blocking.length) return
    const picked = lines.filter((line) => (draft.picked[line.id] ?? 0) > 0 && !isExtra(line))
    const onSale = picked.filter((line) => draft.restock[line.id]).reduce((sum, line) => sum + draft.picked[line.id], 0)
    const notOnSale = picked.filter((line) => draft.restock[line.id] === false).reduce((sum, line) => sum + draft.picked[line.id], 0)
    const parts = [
      onSale ? t.n(onSale, "{n} unit goes back on sale.", "{n} units go back on sale.") : null,
      notOnSale ? t.n(notOnSale, "{n} unit is not put back on sale.", "{n} units are not put back on sale.") : null,
      draft.outcome === "exchange" && size && product
        ? t("{item} is taken out of stock, to send to the customer.", { item: lineName(product.title, size.title) })
        : null,
      refundAmount > 0
        ? card
          ? t("{amount} goes back to the customer's card through Stripe. This is real money and cannot be undone.", { amount: money(refundAmount) })
          : t("The refund is recorded. Give the {amount} back in cash yourself.", { amount: money(refundAmount) })
        : null,
      notify ? t("The customer is emailed.") : t("No email is sent."),
    ]
    const ok = await prompt({
      title: t("Record the return on {ref}?", { ref: order.ref }),
      description: parts.filter(Boolean).join(" "),
      confirmText: refundAmount > 0 ? t("Record and refund {amount}", { amount: money(refundAmount) }) : t("Record the return"),
      cancelText: t("Back"),
      variant: refundAmount > 0 && card ? "danger" : "confirmation",
    })
    if (ok) save.mutate()
  }

  return (
    <div className="border-ui-border-base flex flex-col gap-y-4 border-t pt-3">
      <div className="flex flex-col gap-y-2">
        <Label size="xsmall" weight="plus">{t("What came back")}</Label>
        <ul className="flex flex-col gap-y-3">
          {lines.map((line) => {
            const n = draft.picked[line.id] ?? 0
            const max = line.returnable ?? 0
            const name = lineName(line.title, line.variant ?? "")
            return (
              <li key={line.id} className="flex flex-col gap-y-1.5">
                <div className="flex items-center justify-between gap-x-2">
                  <div className="min-w-0">
                    <Text size="small" weight="plus" className="truncate">{name}</Text>
                    <Text size="xsmall" className="text-ui-fg-muted">{t("{n} sent · {amount} each", { n: max, amount: money(line.paid_unit_price ?? line.unit_price) })}</Text>
                  </div>
                  <div className="flex shrink-0 items-center gap-x-1">
                    <Button size="small" variant="secondary" className="h-9 w-9 p-0" onClick={() => pick(line.id, n - 1, max)} aria-label={t("One less {label}", { label: name })}>−</Button>
                    <span className="hc-data w-8 text-center">{n}</span>
                    <Button size="small" variant="secondary" className="h-9 w-9 p-0" onClick={() => pick(line.id, n + 1, max)} aria-label={t("One more {label}", { label: name })}>+</Button>
                  </div>
                </div>
                {n > 0 && !isExtra(line) && (
                  <Choice
                    label={t("Back on sale?")}
                    value={draft.restock[line.id]}
                    onChange={(v) => setDraft((d) => ({ ...d, restock: { ...d.restock, [line.id]: v } }))}
                    yes={t("Back on sale")}
                    no={t("Not back on sale")}
                  />
                )}
              </li>
            )
          })}
        </ul>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-y-1">
          <Label size="xsmall" weight="plus">{t("Why it came back")}</Label>
          <Select value={draft.reason ?? undefined} onValueChange={(value) => set({ reason: value as Draft["reason"] })}>
            <Select.Trigger><Select.Value placeholder={t("Choose a reason")} /></Select.Trigger>
            <Select.Content>
              {REASONS.map((option) => <Select.Item key={option.key} value={option.key}>{option.label}</Select.Item>)}
            </Select.Content>
          </Select>
        </div>
        <div className="flex flex-col gap-y-1">
          <Label size="xsmall" weight="plus">{t("How it came back (optional)")}</Label>
          <Select value={way ?? undefined} onValueChange={(value) => setWay(value as ReturnWay)}>
            <Select.Trigger><Select.Value placeholder={t("As agreed in a DM")} /></Select.Trigger>
            <Select.Content>
              {WAYS.map((option) => <Select.Item key={option.key} value={option.key}>{option.label}</Select.Item>)}
            </Select.Content>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-y-2">
        <Label size="xsmall" weight="plus">{t("For the customer")}</Label>
        <Tabs
          label={t("For the customer")}
          value={draft.outcome}
          onChange={(key) => set({ outcome: key })}
          items={[
            { key: "refund", label: tc("outcome", "Refund") },
            { key: "exchange", label: tc("outcome", "Exchange") },
          ]}
        />
        {draft.outcome === "refund" ? (
          <div className="flex flex-col gap-y-1">
            <Text size="xsmall" className="text-ui-fg-subtle">
              {card ? t("Paid by card: the refund goes back to the card through Stripe.") : t("Paid in cash: the refund is recorded and you hand the money back.")} {t("Up to {amount}.", { amount: money(refundable) })}
            </Text>
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-y-1">
                <Label htmlFor="return-refund" className="txt-compact-xsmall">{t("Amount €")}</Label>
                <Input id="return-refund" inputMode="decimal" className="w-32" value={draft.refund} onChange={(event) => { setRefundTouched(true); set({ refund: event.target.value }) }} />
              </div>
              {suggestion > 0 && (
                <Button size="small" variant="transparent" onClick={() => { setRefundTouched(false); set({ refund: suggestion.toFixed(2) }) }}>
                  {t("Suggested {amount}", { amount: money(suggestion) })}
                </Button>
              )}
            </div>
            <Text size="xsmall" className="text-ui-fg-muted">{t("The suggestion is what the items were paid, plus the delivery when everything comes back.")}</Text>
          </div>
        ) : (
          <div className="flex flex-col gap-y-2">
            {profit.isLoading && <Text size="xsmall" className="text-ui-fg-muted">{t("Loading…")}</Text>}
            {profit.data && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-y-1">
                    <Label size="xsmall" weight="plus">{t("Product")}</Label>
                    <Select value={productId ?? undefined} onValueChange={(value) => { setProductId(value); set({ exchange: { variant_id: null, quantity: draft.exchange.quantity } }) }}>
                      <Select.Trigger><Select.Value placeholder={t("Choose a product")} /></Select.Trigger>
                      <Select.Content>
                        {choices.map((choice) => <Select.Item key={choice.id} value={choice.id}>{choice.title}</Select.Item>)}
                      </Select.Content>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-y-1">
                    <Label size="xsmall" weight="plus">{t("Size")}</Label>
                    <Select value={draft.exchange.variant_id ?? undefined} onValueChange={(value) => set({ exchange: { variant_id: value, quantity: draft.exchange.quantity } })} disabled={!product}>
                      <Select.Trigger><Select.Value placeholder={t("Choose a size")} /></Select.Trigger>
                      <Select.Content>
                        {(product?.sizes ?? []).map((s) => (
                          <Select.Item key={s.variant_id} value={s.variant_id}>
                            {`${sizeName(s.title) || t("One size")} · ${tc("stocked", "{n} available", { n: s.available ?? 0 })}`}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-x-2">
                  <Label size="xsmall" weight="plus">{t("Units")}</Label>
                  <Button size="small" variant="secondary" className="h-9 w-9 p-0" aria-label={t("One less")} onClick={() => set({ exchange: { ...draft.exchange, quantity: Math.max(1, draft.exchange.quantity - 1) } })}>−</Button>
                  <span className="hc-data w-8 text-center">{draft.exchange.quantity}</span>
                  <Button size="small" variant="secondary" className="h-9 w-9 p-0" aria-label={t("One more")} onClick={() => set({ exchange: { ...draft.exchange, quantity: Math.min(20, draft.exchange.quantity + 1) } })}>+</Button>
                </div>
                {difference != null && draft.exchange.variant_id && (
                  difference > 0 ? (
                    <Text size="xsmall" className="text-ui-fg-subtle">{t("The new item costs {amount} more. Settle it with the customer in a DM (cash, or a payment link).", { amount: money(difference) })}</Text>
                  ) : difference < 0 ? (
                    <div className="flex items-start gap-x-2">
                      <Checkbox id="refund-difference" checked={draft.refundDifference} onCheckedChange={(value) => set({ refundDifference: value === true })} className="mt-0.5" />
                      <Label htmlFor="refund-difference" className="txt-compact-small">{t("Refund the difference ({amount})", { amount: money(Math.min(-difference, refundable)) })}</Label>
                    </div>
                  ) : (
                    <Text size="xsmall" className="text-ui-fg-subtle">{t("Same price: nothing to settle.")}</Text>
                  )
                )}
                <Text size="xsmall" className="text-ui-fg-muted">{t("It is taken out of stock now and shows as To send until you mark it sent.")}</Text>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-y-1">
        <Label htmlFor="return-note" className="txt-compact-xsmall">{t("Note (for you)")}</Label>
        <Input id="return-note" maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
      </div>
      <div className="flex items-start gap-x-2">
        <Checkbox id="return-notify" checked={notify} onCheckedChange={(value) => setNotify(value === true)} className="mt-0.5" />
        <Label htmlFor="return-notify" className="txt-compact-small">
          {order.email != null ? t("Email {email} that the return arrived", { email: order.email }) : t("Email the customer that the return arrived")}
        </Label>
      </div>
      {blocking.length > 0 && (
        <ul className="flex flex-col gap-y-0.5">
          {blocking.map((problem) => <li key={problem}><Text size="xsmall" className="text-ui-fg-muted">{problem}</Text></li>)}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="small" disabled={blocking.length > 0} isLoading={save.isPending} onClick={() => void confirm()}>{t("Record the return")}</Button>
        <Button size="small" variant="secondary" onClick={onClose}>{t("Cancel")}</Button>
      </div>
    </div>
  )
}
